import { Card } from "design-system/molecules/Card"
import { ChartConfig } from "design-system/molecules/Chart"
import { Tabs, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import {
  ComparisonActivityChart,
  ComparisonChartConfig,
} from "design-system/organisms/insights/ComparisonActivityChart"
import { NpsActivityChart } from "design-system/organisms/insights/NpsActivityChart"
import { MINUTES_SAVED_PER_QUESTION } from "lopermut"
import { useActivityQuery } from "modules/dashboard/hooks/useActivityQuery"
import { useDeflectionActivityQuery } from "modules/dashboard/hooks/useDeflectionActivityQuery"
import { useNpsActivityQuery } from "modules/dashboard/hooks/useNpsActivityQuery"
import { useSupportActivityQuery } from "modules/dashboard/hooks/useSupportActivityQuery"
import {
  useActionRequestsActivityQuery,
  useIssueDeflectionActivityQuery,
  useQuestionsActivityQuery,
} from "modules/dashboard/hooks/useSupportEntityActivityQueries"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import React from "react"
import { useTranslation } from "react-i18next"
import { GetMetricSchema, TimeDimension, TimeFrame } from "schema"
import { z } from "zod"
import { DashboardMetrics } from "./DashboardMetrics"

type Props = {
  timeFrame: TimeFrame
  timeDimension: TimeDimension
  filters: Omit<z.input<typeof GetMetricSchema>, "timeFrame">
  isNewSupportFlowEnabled: boolean
}

type SupportHoursChart = "questions" | "actionRequests"

export const Activity = ({ timeFrame, timeDimension, filters, isNewSupportFlowEnabled }: Props) => {
  const { t } = useTranslation()
  const query = { ...filters, timeFrame, timeDimension }
  const { developerMode } = useDeveloperMode()
  const shouldShowLegacySupport = !isNewSupportFlowEnabled
  const shouldShowNewSupport = isNewSupportFlowEnabled || developerMode
  const legacyContainmentMetricIdx = shouldShowLegacySupport ? 1 : null
  const legacyTimeSavedMetricIdx = shouldShowLegacySupport ? 2 : null
  const issueDeflectionMetricIdx = shouldShowLegacySupport ? 3 : 1
  const supportEntityHoursSavedMetricIdx = issueDeflectionMetricIdx + 1
  const npsMetricIdx = shouldShowNewSupport
    ? supportEntityHoursSavedMetricIdx + 1
    : issueDeflectionMetricIdx
  const numberOfMetrics = npsMetricIdx + 1

  const [activeMetricIdx, setActiveMetricIdx] = React.useState(0)
  const [supportHoursChart, setSupportHoursChart] = React.useState<SupportHoursChart>("questions")
  const [hoveredMetrics, setHoveredMetrics] = React.useState(() => ({
    ...Array.from({ length: 7 }, () => false),
    [activeMetricIdx]: true,
  }))

  React.useEffect(() => {
    if (activeMetricIdx >= numberOfMetrics) setActiveMetricIdx(0)
  }, [activeMetricIdx, numberOfMetrics])

  const { data: engagementData, isPending: isEngagementPending } = useActivityQuery({
    query,
    enabled: hoveredMetrics[0] || activeMetricIdx === 0,
  })
  const { data: deflectionData, isPending: isDeflectionPending } = useDeflectionActivityQuery({
    query,
    enabled:
      legacyContainmentMetricIdx !== null &&
      (hoveredMetrics[legacyContainmentMetricIdx] ||
        activeMetricIdx === legacyContainmentMetricIdx),
  })
  const { data: supportData, isPending: isSupportPending } = useSupportActivityQuery({
    query,
    enabled:
      legacyTimeSavedMetricIdx !== null &&
      (hoveredMetrics[legacyTimeSavedMetricIdx] || activeMetricIdx === legacyTimeSavedMetricIdx),
  })
  const { data: npsData, isPending: isNpsPending } = useNpsActivityQuery({
    query,
    enabled: hoveredMetrics[npsMetricIdx] || activeMetricIdx === npsMetricIdx,
  })
  const { data: issueDeflectionData, isPending: isIssueDeflectionPending } =
    useIssueDeflectionActivityQuery({
      query,
      enabled:
        shouldShowNewSupport &&
        (hoveredMetrics[issueDeflectionMetricIdx] || activeMetricIdx === issueDeflectionMetricIdx),
    })
  const { data: questionsData, isPending: isQuestionsPending } = useQuestionsActivityQuery({
    query,
    enabled:
      shouldShowNewSupport &&
      (hoveredMetrics[supportEntityHoursSavedMetricIdx] ||
        activeMetricIdx === supportEntityHoursSavedMetricIdx),
  })
  const { data: actionRequestsData, isPending: isActionRequestsPending } =
    useActionRequestsActivityQuery({
      query,
      enabled:
        shouldShowNewSupport &&
        (hoveredMetrics[supportEntityHoursSavedMetricIdx] ||
          activeMetricIdx === supportEntityHoursSavedMetricIdx),
    })

  const supportHoursSavedMaxValue = Math.max(
    0,
    ...(questionsData?.dailyCount.map((item) => getHoursSaved(item.topValue)) ?? []),
    ...(actionRequestsData?.dailyCount.map((item) => getHoursSaved(item.topValue)) ?? []),
  )
  const supportHoursSavedDomainMax =
    supportHoursSavedMaxValue > 0 ? supportHoursSavedMaxValue * 1.1 : 1

  const noDataT = {
    title: t("dashboard.activity.noData.title"),
    description: t("dashboard.activity.noData.description"),
  }

  const engagementChartConfig = {
    contacted: {
      label: t("dashboard.activity.contacted"),
      color: "var(--chart-secondary)",
      type: "bar",
    },
    engaged: {
      label: t("dashboard.activity.engaged"),
      color: "var(--chart-primary)",
      type: "bar",
    },
    achieveRate: {
      label: t("shared.rates.achieveRate"),
      color: "var(--chart-3)",
      type: "line",
    },
  } satisfies ComparisonChartConfig

  const deflectionChartConfig = {
    inbound: {
      label: t("dashboard.activity.inbound"),
      color: "var(--chart-secondary)",
      type: "bar",
    },
    deflected: {
      label: t("dashboard.activity.deflected"),
      color: "var(--chart-primary)",
      type: "bar",
    },
    containmentRate: {
      label: t("dashboard.metrics.containmentRate.title"),
      color: "var(--success)",
      type: "line",
    },
  } satisfies ComparisonChartConfig

  const supportChartConfig = {
    questions: {
      label: t("dashboard.activity.questions"),
      color: "var(--support-secondary)",
      type: "bar",
    },
    solved: {
      label: t("dashboard.activity.solved"),
      color: "var(--support-primary)",
      type: "bar",
    },
    solveRate: {
      label: t("dashboard.support.solveRate"),
      color: "var(--chart-2)",
      type: "line",
    },
  } satisfies ComparisonChartConfig

  const issueDeflectionChartConfig = {
    inbound: {
      label: t("dashboard.activity.inbound"),
      color: "var(--chart-secondary)",
      type: "bar",
    },
    withoutIssue: {
      label: t("dashboard.activity.withoutIssue"),
      color: "var(--chart-primary)",
      type: "bar",
    },
    deflectionRate: {
      label: t("dashboard.metrics.issueDeflectionRate.title"),
      color: "var(--success)",
      type: "line",
    },
  } satisfies ComparisonChartConfig

  const questionsChartConfig = {
    questions: {
      label: t("dashboard.activity.questions"),
      color: "color-mix(in srgb, var(--chart-4) 25%, transparent)",
      type: "bar",
    },
    answered: {
      label: t("dashboard.tooltip.activity.autoAnswered"),
      color: "var(--chart-4)",
      type: "bar",
    },
    autoAnswerRate: {
      label: t("dashboard.tooltip.activity.autoAnswerRate"),
      color: "var(--chart-1)",
      type: "line",
    },
    hoursSaved: {
      label: t("dashboard.metrics.supportEntityHoursSaved.chartLabel"),
      color: "var(--testing-3)",
      type: "line",
      valueType: "secondary",
    },
  } satisfies ComparisonChartConfig

  const actionRequestsChartConfig = {
    requests: {
      label: t("dashboard.tooltip.activity.requests"),
      color: "var(--chart-2)",
      type: "bar",
    },
    fulfilled: {
      label: t("dashboard.tooltip.activity.fulfilled"),
      color: "var(--chart-3)",
      type: "bar",
    },
    fulfillmentRate: {
      label: t("dashboard.activity.autoFulfillmentRate"),
      color: "var(--chart-1)",
      type: "line",
    },
    hoursSaved: {
      label: t("dashboard.metrics.supportEntityHoursSaved.chartLabel"),
      color: "var(--testing-3)",
      type: "line",
      valueType: "secondary",
    },
  } satisfies ComparisonChartConfig

  const npsChartConfig = {
    detractors: { label: t("shared.nps.detractors") },
    passives: { label: t("shared.nps.passives") },
    promoters: { label: t("shared.nps.promoters") },
    score: { label: t("shared.nps.score") },
  } satisfies ChartConfig

  const supportHoursChartSelector = (
    <Tabs
      variant="pill"
      value={supportHoursChart}
      onValueChange={(value) => setSupportHoursChart(value as SupportHoursChart)}
    >
      <TabsList>
        <TabsTrigger value="questions">{t("dashboard.question.title")}</TabsTrigger>
        <TabsTrigger value="actionRequests">{t("dashboard.actionRequest.title")}</TabsTrigger>
      </TabsList>
    </Tabs>
  )

  return (
    <Card className="flex flex-col min-w-xs">
      <div className="flex flex-col gap-4 p-6">
        <DashboardMetrics
          timeFrame={timeFrame}
          filters={filters}
          activeMetricIdx={activeMetricIdx}
          setActiveMetricIdx={setActiveMetricIdx}
          onHoverIdx={(index) => setHoveredMetrics((prev) => ({ ...prev, [index]: true }))}
          npsMetricIdx={npsMetricIdx}
          legacyContainmentMetricIdx={legacyContainmentMetricIdx}
          legacyTimeSavedMetricIdx={legacyTimeSavedMetricIdx}
          shouldShowNewSupport={shouldShowNewSupport}
          issueDeflectionMetricIdx={issueDeflectionMetricIdx}
          supportEntityHoursSavedMetricIdx={supportEntityHoursSavedMetricIdx}
        />
        {activeMetricIdx === 0 && (
          <ComparisonActivityChart
            data={engagementData?.dailyCount.map((d) => ({
              date: d.date,
              contacted: d.baseValue,
              engaged: d.topValue,
              achieveRate: d.rate,
            }))}
            chartConfig={engagementChartConfig}
            isPending={isEngagementPending}
            noDataT={noDataT}
          />
        )}
        {legacyContainmentMetricIdx !== null && activeMetricIdx === legacyContainmentMetricIdx && (
          <ComparisonActivityChart
            data={deflectionData?.dailyCount.map((d) => ({
              date: d.date,
              inbound: d.baseValue,
              deflected: d.topValue,
              containmentRate: d.rate !== undefined ? Math.round(d.rate * 100) : d.rate,
            }))}
            chartConfig={deflectionChartConfig}
            isPending={isDeflectionPending}
            noDataT={noDataT}
            legendTooltipByKey={{
              containmentRate: t("dashboard.tooltip.activity.containmentRateLegendTooltip"),
            }}
          />
        )}
        {legacyTimeSavedMetricIdx !== null && activeMetricIdx === legacyTimeSavedMetricIdx && (
          <ComparisonActivityChart
            data={supportData?.dailyCount.map((d) => ({
              date: d.date,
              questions: d.baseValue,
              solved: d.topValue,
              solveRate: d.baseValue ? Math.round((d.topValue / d.baseValue) * 100) : undefined,
            }))}
            chartConfig={supportChartConfig}
            isPending={isSupportPending}
            noDataT={noDataT}
          />
        )}
        {activeMetricIdx === npsMetricIdx && (
          <NpsActivityChart
            data={npsData?.dailyCount}
            chartConfig={npsChartConfig}
            isPending={isNpsPending}
            noDataT={noDataT}
          />
        )}
        {shouldShowNewSupport && activeMetricIdx === issueDeflectionMetricIdx && (
          <ComparisonActivityChart
            data={issueDeflectionData?.dailyCount.map((d) => ({
              date: d.date,
              inbound: d.baseValue,
              withoutIssue: d.topValue,
              deflectionRate: d.rate !== undefined ? Math.round(d.rate * 100) : d.rate,
            }))}
            chartConfig={issueDeflectionChartConfig}
            isPending={isIssueDeflectionPending}
            noDataT={noDataT}
          />
        )}
        {shouldShowNewSupport &&
          activeMetricIdx === supportEntityHoursSavedMetricIdx &&
          supportHoursChart === "questions" && (
            <ComparisonActivityChart
              data={questionsData?.dailyCount.map((d) => ({
                date: d.date,
                questions: d.baseValue,
                answered: d.topValue,
                autoAnswerRate: d.rate !== undefined ? Math.round(d.rate * 100) : d.rate,
                hoursSaved: getHoursSaved(d.topValue),
              }))}
              chartConfig={questionsChartConfig}
              isPending={isQuestionsPending}
              noDataT={noDataT}
              action={supportHoursChartSelector}
              secondaryMaxValue={supportHoursSavedDomainMax}
            />
          )}
        {shouldShowNewSupport &&
          activeMetricIdx === supportEntityHoursSavedMetricIdx &&
          supportHoursChart === "actionRequests" && (
            <ComparisonActivityChart
              data={actionRequestsData?.dailyCount.map((d) => ({
                date: d.date,
                requests: d.baseValue,
                fulfilled: d.topValue,
                fulfillmentRate: d.rate !== undefined ? Math.round(d.rate * 100) : d.rate,
                hoursSaved: getHoursSaved(d.topValue),
              }))}
              chartConfig={actionRequestsChartConfig}
              isPending={isActionRequestsPending}
              noDataT={noDataT}
              action={supportHoursChartSelector}
              secondaryMaxValue={supportHoursSavedDomainMax}
            />
          )}
      </div>
    </Card>
  )
}

function getHoursSaved(completedCount: number) {
  return Math.round((completedCount * MINUTES_SAVED_PER_QUESTION) / 60)
}
